"use client";

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Link from "next/link";
import {
  AuthCard,
  AuthField,
  AuthLink,
  AuthStatus,
  authFieldClassName,
  authPrimaryClass,
} from "@/components/auth/auth-card";
import { PasswordField } from "@/components/auth/password-field";
import { authClient } from "@/lib/auth-client";
import { registerSchema } from "@/modules/account/schemas";

type RegisterValues = {
  name: string;
  email: string;
  password: string;
};

export function RegisterForm({ invited = false }: { invited?: boolean }) {
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: "", email: "", password: "" },
  });

  async function onSubmit(values: RegisterValues) {
    setFormError(null);
    const result = await authClient.signUp.email({
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      password: values.password,
      callbackURL: "/app",
    });

    if (result.error) {
      setFormError("Не удалось создать аккаунт. Проверьте данные и попробуйте ещё раз.");
      return;
    }

    setDone(true);
  }

  if (done) {
    return (
      <AuthCard
        title="Проверьте почту"
        description="Если этот адрес можно использовать, мы отправили письмо со ссылкой для подтверждения. После перехода по ссылке откроется ваш кабинет."
        footer={
          <>
            Уже подтвердили? <AuthLink href="/login">Войти</AuthLink>
          </>
        }
      >
        <p className="text-sm leading-6 text-[var(--m-muted)]">
          Письмо могло попасть в спам. Если его нет, откройте страницу подтверждения и запросите
          письмо ещё раз.
        </p>
        <Link href="/verify-email" className={`${authPrimaryClass} mt-4`}>
          К подтверждению
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Создать аккаунт"
      description="Начните работу в Sverim — первый проект запустит 14-дневный пробный период."
      footer={
        <>
          Уже есть аккаунт? <AuthLink href="/login">Войти</AuthLink>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        {invited ? (
          <AuthStatus tone="info">
            <p className="font-semibold">Вас пригласили в организацию</p>
            <p className="mt-0.5">После подтверждения email вы сможете принять приглашение.</p>
          </AuthStatus>
        ) : null}
        <AuthField label="Имя" htmlFor="name" error={form.formState.errors.name?.message}>
          <input
            id="name"
            type="text"
            autoComplete="name"
            className={authFieldClassName}
            aria-invalid={Boolean(form.formState.errors.name) || undefined}
            aria-describedby={form.formState.errors.name ? "name-error" : undefined}
            {...form.register("name")}
          />
        </AuthField>
        <AuthField label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className={authFieldClassName}
            aria-invalid={Boolean(form.formState.errors.email) || undefined}
            aria-describedby={form.formState.errors.email ? "email-error" : undefined}
            {...form.register("email")}
          />
        </AuthField>
        <AuthField
          label="Пароль"
          htmlFor="password"
          error={form.formState.errors.password?.message}
          hint="Минимум 10 символов."
        >
          <PasswordField
            id="password"
            autoComplete="new-password"
            invalid={Boolean(form.formState.errors.password)}
            registration={form.register("password")}
            variant="auth"
          />
        </AuthField>
        {formError ? <AuthStatus tone="error">{formError}</AuthStatus> : null}
        <button type="submit" className={authPrimaryClass} disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? "Создаём аккаунт…" : "Создать аккаунт"}
        </button>
      </form>
    </AuthCard>
  );
}
